// InsuranceUploadPage.tsx - MUI Version
import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useUploadHarHabituach } from '../../hooks/insurance_data/useInsuranceData';


interface UploadResult {
  success: number;
  failed: Array<[string, string]>;
}

interface InsuranceUploadProps {
  onUploadComplete?: (result: UploadResult) => void;
}

export default function InsuranceUpload({ onUploadComplete }: InsuranceUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  
  const uploadMutation = useUploadHarHabituach();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, []);

  const handleFileUpload = async (file: File) => {
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      alert('אנא העלה קובץ Excel בלבד (.xlsx או .xls)');
      return;
    }

    setUploadResult(null);

    try {
      const result = await uploadMutation.mutateAsync(file);
      setUploadResult(result);
      onUploadComplete?.(result);
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const isUploading = uploadMutation.isPending;

  return (
    <Box maxWidth="lg" mx="auto" p={3} sx={{ '& > *:not(:last-child)': { mb: 3 } }}>
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h5" fontWeight="bold">
            העלאת קובץ הר הביטוח
          </Typography>
          {uploadResult && (
            <Button
              size="small"
              onClick={() => {
                setUploadResult(null);
                uploadMutation.reset();
              }}
            >
              העלאת קובץ חדש
            </Button>
          )}
        </Box>
        
        {/* Drag and Drop Zone */}
        <Paper
          elevation={0}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            position: 'relative',
            border: '2px dashed',
            borderColor: isDragging ? 'primary.main' : 'grey.300',
            bgcolor: isDragging ? 'primary.lighter' : 'grey.50',
            borderRadius: 2,
            p: 1,
            textAlign: 'center',
            transition: 'all 0.3s',
            cursor: isUploading ? 'not-allowed' : 'pointer',
            opacity: isUploading ? 0.5 : 1,
            '&:hover': !isUploading ? {
              borderColor: 'primary.light',
              bgcolor: 'primary.lighter',
            } : {},
          }}
        >
          <input
            type="file"
            onChange={handleFileSelect}
            accept=".xlsx,.xls"
            disabled={isUploading}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              cursor: isUploading ? 'not-allowed' : 'pointer',
            }}
          />
          
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            {isUploading ? (
              <>
                <CircularProgress size={48} />
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
              </>
            ) : (
              <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            )}
            
            <Box>
              <Typography variant="h6" color="text.primary">
                {isUploading ? 'מעלה קובץ...' : 'גרור קובץ לכאן או לחץ לבחירה'}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                קבצי Excel בלבד (.xlsx, .xls)
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Upload Result Summary */}
        {uploadResult && (
          <Alert 
            severity={uploadResult.failed.length > 0 ? 'warning' : 'success'}
            icon={uploadResult.failed.length > 0 ? <WarningIcon /> : <CheckCircleIcon />}
            sx={{ mt: 3 }}
          >
            <AlertTitle>
              הועלו {uploadResult.success} פוליסות בהצלחה
            </AlertTitle>
            {uploadResult.failed.length > 0 && (
              <Typography variant="body2">
                {uploadResult.failed.length} פוליסות נכשלו
              </Typography>
            )}
          </Alert>
        )}

        {/* Error Message */}
        {uploadMutation.isError && (
          <Alert severity="error" icon={<ErrorIcon />} sx={{ mt: 3 }}>
            <AlertTitle>שגיאה בהעלאת הקובץ</AlertTitle>
            <Typography variant="body2">
              {uploadMutation.error instanceof Error 
                ? uploadMutation.error.message 
                : 'אירעה שגיאה לא צפויה'}
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* Failed Items */}
      {uploadResult && uploadResult.failed.length > 0 && (
        <Paper 
          elevation={0} 
          sx={{ 
            border: '1px solid', 
            borderColor: 'error.light',
            borderRadius: 2, 
            p: 3 
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <ErrorIcon color="error" />
            <Typography variant="h6" fontWeight="bold" color="error.dark">
              אנשי קשר שלא נמצאו במערכת ({uploadResult.failed.length})
            </Typography>
          </Box>
          
          <List 
            sx={{ 
              maxHeight: 100, 
              overflow: 'auto',
              '& .MuiListItem-root': {
                bgcolor: 'error.lighter',
                border: '1px solid',
                borderColor: 'error.light',
                borderRadius: 1,
                mb: 1,
              }
            }}
          >
            {uploadResult.failed.map(([contactId, error], index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={
                    <Typography variant="body1" fontWeight={500} color="error.dark">
                      תעודת זהות: {contactId}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body2" color="error.main" mt={0.5}>
                      {error}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}